import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import StrainImageManager from './StrainImageManager';

const TYPE_COLORS = {
  indica: { background: '#6b46c1', color: '#d6bcfa' },
  sativa: { background: '#2b6cb0', color: '#bee3f8' },
  hybrid: { background: '#c05621', color: '#fefcbf' },
};

const PRICE_FIELDS = [
  { priceKey: 'price_1g', costKey: 'cost_1g', label: '1g' },
  { priceKey: 'price_2g', costKey: 'cost_2g', label: '2g' },
  { priceKey: 'price_3_5g', costKey: 'cost_3_5g', label: '3.5g' },
  { priceKey: 'price_7g', costKey: 'cost_7g', label: '7g' },
  { priceKey: 'price_14g', costKey: 'cost_14g', label: '14g' },
];

export default function StrainManager() {
  const [strains, setStrains] = useState([]);
  const [editId, setEditId] = useState(null);
  const [pricingId, setPricingId] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'hybrid', description: '', stock_grams: 0, total_cost: '' });
  const [priceForm, setPriceForm] = useState({});
  const [restockId, setRestockId] = useState(null);
  const [restockForm, setRestockForm] = useState({ add_grams: '', add_cost: '' });
  const [writeOffId, setWriteOffId] = useState(null);
  const [writeOffResult, setWriteOffResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchStrains = () => {
    api.get('/admin/strains/all').then(setStrains).catch(() => {});
  };

  useEffect(() => { fetchStrains(); }, []);

  const resetForm = () => {
    setForm({ name: '', type: 'hybrid', description: '', stock_grams: 0, total_cost: '' });
    setEditId(null);
    setError('');
  };

  const handleEdit = (strain) => {
    setEditId(strain.id);
    setPricingId(null);
    setForm({
      name: strain.name,
      type: strain.type,
      description: strain.description || '',
      stock_grams: strain.stock_grams,
      total_cost: strain.total_cost || '',
    });
  };

  const handlePricing = (strain) => {
    setPricingId(pricingId === strain.id ? null : strain.id);
    setEditId(null);
    setPriceForm({
      price_1g: strain.price_1g || '',
      price_2g: strain.price_2g || '',
      price_3_5g: strain.price_3_5g || '',
      price_7g: strain.price_7g || '',
      price_14g: strain.price_14g || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/strains/${editId}`, form);
      } else {
        await api.post('/admin/strains', form);
      }
      resetForm();
      fetchStrains();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePriceSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/strains/${pricingId}/prices`, priceForm);
      setPricingId(null);
      fetchStrains();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this strain?')) return;
    try {
      await api.delete(`/admin/strains/${id}`);
      fetchStrains();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestock = (strain) => {
    setRestockId(restockId === strain.id ? null : strain.id);
    setEditId(null);
    setPricingId(null);
    setRestockForm({ add_grams: '', add_cost: '' });
  };

  const handleRestockSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/strains/${restockId}/restock`, restockForm);
      setRestockId(null);
      setRestockForm({ add_grams: '', add_cost: '' });
      fetchStrains();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWriteOff = (strain) => {
    setWriteOffId(writeOffId === strain.id ? null : strain.id);
    setWriteOffResult(null);
    setEditId(null);
    setPricingId(null);
    setRestockId(null);
  };

  const handleWriteOffConfirm = async (strainId) => {
    setSaving(true);
    setError('');
    try {
      const result = await api.put(`/admin/strains/${strainId}/write-off`);
      setWriteOffResult(result.write_off);
      fetchStrains();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStock = async (strain) => {
    try {
      await api.put(`/admin/strains/${strain.id}`, { is_in_stock: !strain.is_in_stock });
      fetchStrains();
    } catch (err) {
      setError(err.message);
    }
  };

  // Calculate cost per gram preview
  const costPerGram = form.total_cost && form.stock_grams > 0
    ? (parseFloat(form.total_cost) / form.stock_grams).toFixed(2)
    : null;

  const WEIGHTS = [1, 2, 3.5, 7, 14];
  const totalStock = strains.reduce((sum, s) => sum + (parseFloat(s.stock_grams) || 0), 0);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Strain Inventory</h3>
        {editId && (
          <button className="btn-secondary btn-small" onClick={resetForm}>Cancel Edit</button>
        )}
      </div>

      {/* Inventory Summary */}
      {strains.length > 0 && (
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
          padding: 12, marginBottom: 16, border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--success)' }}>{totalStock}g</span>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>total stock</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 6 }}>
            {WEIGHTS.map(w => (
              <div key={w} style={{
                background: 'var(--bg-primary)', borderRadius: 'var(--radius-sm)',
                padding: '8px 6px', textAlign: 'center'
              }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {Math.floor(totalStock / w)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{w}g bags</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Add/Edit Form */}
      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 8 }}>
          <input
            placeholder="Strain name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            required
          />
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="indica">Indica</option>
            <option value="sativa">Sativa</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <input
            type="number"
            placeholder="Stock (g)"
            value={form.stock_grams}
            onChange={e => setForm(f => ({ ...f, stock_grams: parseInt(e.target.value) || 0 }))}
            min="0"
          />
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="Total cost (£)"
            value={form.total_cost}
            onChange={e => setForm(f => ({ ...f, total_cost: e.target.value }))}
          />
        </div>
        {costPerGram && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            Cost per gram: <strong style={{ color: 'var(--text-secondary)' }}>£{costPerGram}/g</strong>
          </div>
        )}
        <input
          placeholder="Description (optional)"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          style={{ marginBottom: 8 }}
        />
        <button type="submit" className="btn-primary btn-small" disabled={saving}>
          {saving ? 'Saving...' : editId ? 'Update Strain' : 'Add Strain'}
        </button>
      </form>

      {/* Image Manager (when editing) */}
      {editId && (() => {
        const strain = strains.find(s => s.id === editId);
        return strain ? (
          <StrainImageManager strain={strain} onUpdate={fetchStrains} />
        ) : null;
      })()}

      {/* Pricing Form (inline) - sale prices only, costs auto-calculated */}
      {pricingId && (
        <form onSubmit={handlePriceSubmit} style={{
          marginBottom: 16, padding: 12, background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h4 style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              Sale Prices: {strains.find(s => s.id === pricingId)?.name}
            </h4>
            <button type="button" className="btn-secondary btn-small" onClick={() => setPricingId(null)} style={{ padding: '3px 8px', fontSize: 11 }}>Close</button>
          </div>
          {(() => {
            const s = strains.find(s => s.id === pricingId);
            const startingStock = parseFloat(s?.starting_stock_grams) || 0;
            const cpg = s && parseFloat(s.total_cost) > 0 && startingStock > 0
              ? parseFloat(s.total_cost) / startingStock : null;
            return (
              <>
                {cpg && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                    Cost per gram: £{cpg.toFixed(2)}/g (auto-calculated from total cost)
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                  {PRICE_FIELDS.map(f => {
                    const grams = parseFloat(f.label);
                    const costVal = cpg ? (cpg * grams).toFixed(2) : null;
                    return (
                      <div key={f.priceKey} style={{ fontSize: 12 }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{f.label}</div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Sale price"
                          value={priceForm[f.priceKey] || ''}
                          onChange={e => setPriceForm(p => ({ ...p, [f.priceKey]: e.target.value }))}
                          style={{ fontSize: 12, padding: '4px 6px', marginBottom: 2 }}
                        />
                        {costVal && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            Cost: £{costVal}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <button type="submit" className="btn-primary btn-small" disabled={saving} style={{ marginTop: 8 }}>
            {saving ? 'Saving...' : 'Save Prices'}
          </button>
        </form>
      )}

      {/* Restock Form (inline) */}
      {restockId && (() => {
        const strain = strains.find(s => s.id === restockId);
        const addGrams = parseFloat(restockForm.add_grams) || 0;
        const addCost = parseFloat(restockForm.add_cost) || 0;
        const newTotal = (parseFloat(strain?.stock_grams) || 0) + addGrams;
        const newCost = (parseFloat(strain?.total_cost) || 0) + addCost;
        const newStartingStock = (parseFloat(strain?.starting_stock_grams) || 0) + addGrams;
        const newCpg = newCost > 0 && newStartingStock > 0 ? (newCost / newStartingStock).toFixed(2) : null;
        return (
          <form onSubmit={handleRestockSubmit} style={{
            marginBottom: 16, padding: 12, background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h4 style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Restock: {strain?.name}
              </h4>
              <button type="button" className="btn-secondary btn-small" onClick={() => setRestockId(null)} style={{ padding: '3px 8px', fontSize: 11 }}>Close</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Current: {parseFloat(strain?.stock_grams) || 0}g / £{parseFloat(strain?.total_cost || 0).toFixed(2)} cost
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Add grams</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  placeholder="Grams to add"
                  value={restockForm.add_grams}
                  onChange={e => setRestockForm(f => ({ ...f, add_grams: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Cost (£)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Cost of new stock"
                  value={restockForm.add_cost}
                  onChange={e => setRestockForm(f => ({ ...f, add_cost: e.target.value }))}
                />
              </div>
            </div>
            {addGrams > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                After restock: <strong style={{ color: 'var(--success)' }}>{newTotal}g</strong>
                {addCost > 0 && (
                  <> / £{newCost.toFixed(2)} cost</>
                )}
                {newCpg && (
                  <> — <strong style={{ color: 'var(--text-secondary)' }}>£{newCpg}/g</strong> avg</>
                )}
              </div>
            )}
            <button type="submit" className="btn-primary btn-small" disabled={saving}>
              {saving ? 'Restocking...' : 'Add Stock'}
            </button>
          </form>
        );
      })()}

      {/* Write Off Panel (inline) */}
      {writeOffId && (() => {
        const strain = strains.find(s => s.id === writeOffId);
        const stock = parseFloat(strain?.stock_grams) || 0;
        const totalCost = parseFloat(strain?.total_cost) || 0;
        const startingStock = parseFloat(strain?.starting_stock_grams) || 0;
        const cpg = startingStock > 0 && totalCost > 0 ? (totalCost / startingStock).toFixed(2) : '0.00';
        const writeOffCost = stock * parseFloat(cpg);
        return (
          <div style={{
            marginBottom: 16, padding: 12, background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--danger)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h4 style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}>
                Write Off: {strain?.name}
              </h4>
              <button type="button" className="btn-secondary btn-small" onClick={() => { setWriteOffId(null); setWriteOffResult(null); }} style={{ padding: '3px 8px', fontSize: 11 }}>Close</button>
            </div>

            {writeOffResult ? (
              <div style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 8 }}>Stock written off successfully</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>Grams written off: <strong>{writeOffResult.grams_written_off}g</strong></div>
                  <div>Cost written off: <strong style={{ color: 'var(--danger)' }}>£{parseFloat(writeOffResult.cost_written_off).toFixed(2)}</strong></div>
                  <div>Revenue earned: <strong style={{ color: 'var(--success)' }}>£{parseFloat(writeOffResult.total_revenue_earned).toFixed(2)}</strong></div>
                  <div>Net loss: <strong style={{ color: writeOffResult.net_loss > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {writeOffResult.net_loss > 0 ? `£${parseFloat(writeOffResult.net_loss).toFixed(2)}` : 'None (profit made)'}
                  </strong></div>
                </div>
              </div>
            ) : (
              <>
                {stock <= 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No stock to write off.</div>
                ) : (
                  <>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
                      This will zero out the remaining stock and record the loss.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13, marginBottom: 12 }}>
                      <div>Remaining stock: <strong>{stock}g</strong></div>
                      <div>Cost per gram: <strong>£{cpg}/g</strong></div>
                      <div>Total cost to write off: <strong style={{ color: 'var(--danger)' }}>£{writeOffCost.toFixed(2)}</strong></div>
                    </div>
                    <button
                      className="btn-small"
                      onClick={() => handleWriteOffConfirm(writeOffId)}
                      disabled={saving}
                      style={{ background: 'var(--danger)', color: '#fff', padding: '6px 14px', fontSize: 12 }}
                    >
                      {saving ? 'Writing off...' : `Write Off ${stock}g (£${writeOffCost.toFixed(2)} loss)`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Strains Table */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Stock</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {strains.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', opacity: 0.5 }}>No strains yet</td></tr>
            )}
            {strains.map(s => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td>
                  <span className="badge" style={TYPE_COLORS[s.type]}>
                    {s.type}
                  </span>
                </td>
                <td>
                  {parseFloat(s.stock_grams)}g
                  {parseFloat(s.stock_grams) > 0 && parseFloat(s.stock_grams) < 10 && (
                    <span className="badge" style={{ background: '#c05621', color: '#fefcbf', marginLeft: 6, fontSize: 9 }}>LOW</span>
                  )}
                </td>
                <td>
                  <button
                    className={`badge ${s.is_in_stock ? 'badge-success' : 'badge-redeemed'}`}
                    onClick={() => toggleStock(s)}
                    style={{ cursor: 'pointer', border: 'none' }}
                    title="Click to toggle"
                  >
                    {s.is_in_stock ? 'In Stock' : 'Out'}
                  </button>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-small btn-secondary" onClick={() => handleEdit(s)}>Edit</button>
                    <button className="btn-small" onClick={() => handleRestock(s)} style={{ background: 'var(--success)', color: '#fff', padding: '4px 8px', fontSize: 11 }}>Restock</button>
                    <button className="btn-small btn-primary" onClick={() => handlePricing(s)} style={{ padding: '4px 8px', fontSize: 11 }}>Pricing</button>
                    {parseFloat(s.stock_grams) > 0 && (
                      <button className="btn-small" onClick={() => handleWriteOff(s)} style={{ background: 'var(--warning)', color: '#000', padding: '4px 8px', fontSize: 11 }}>W/O</button>
                    )}
                    <button className="btn-small btn-danger" onClick={() => handleDelete(s.id)}>Del</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
